import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport';
import { RpcError } from '@protobuf-ts/runtime-rpc';

export const isNotFoundError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.NOT_FOUND]
  );
};

/** Any gRPC `PERMISSION_DENIED`, including payment-required and login-method-denied. */
export const isPermissionDeniedError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.PERMISSION_DENIED]
  );
};

/**
 * `PERMISSION_DENIED` that should show the generic "you need access" page.
 *
 * The backend maps several distinct outcomes onto the same gRPC code.
 * Payment-required and login-method-denied have their own pages, so they are
 * excluded here. This is not the same as HTTP 401; gRPC-web still reports 200.
 */
export const isNotAuthorizedError = (error: unknown): error is RpcError => {
  return (
    isPermissionDeniedError(error) &&
    !error.message.includes('errPaymentRequiredForOrganization') &&
    !error.message.includes('errLoginMethodDeniedForOrganization')
  );
};

/**
 * gRPC-web always returns HTTP 200, even when the RPC failed. The real outcome
 * is in `RpcError.code` (the gRPC status). TanStack Query retries thrown errors
 * by default, so retryability must be decided from that gRPC code — not the
 * HTTP status shown in DevTools.
 *
 * Permission errors (no access, machine/org mismatch, payment required, login
 * method denied) and other client-fault codes are not recoverable.
 */
const NON_RETRYABLE_GRPC_STATUS_NAMES = new Set<string>([
  GrpcStatusCode[GrpcStatusCode.PERMISSION_DENIED],
  GrpcStatusCode[GrpcStatusCode.NOT_FOUND],
  GrpcStatusCode[GrpcStatusCode.INVALID_ARGUMENT],
  GrpcStatusCode[GrpcStatusCode.FAILED_PRECONDITION],
  GrpcStatusCode[GrpcStatusCode.UNIMPLEMENTED],
  GrpcStatusCode[GrpcStatusCode.ALREADY_EXISTS],
  GrpcStatusCode[GrpcStatusCode.OUT_OF_RANGE],
  GrpcStatusCode[GrpcStatusCode.CANCELLED],
]);

export const isNonRetryableQueryError = (error: unknown): boolean => {
  return (
    error instanceof RpcError && NON_RETRYABLE_GRPC_STATUS_NAMES.has(error.code)
  );
};

export const isUnauthenticatedError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED]
  );
};

export const isPaymentRequiredError = (error: unknown): error is RpcError => {
  return (
    isPermissionDeniedError(error) &&
    error.message.includes('errPaymentRequiredForOrganization')
  );
};

export const isLoginMethodDeniedError = (error: unknown): error is RpcError => {
  return (
    isPermissionDeniedError(error) &&
    error.message.includes('errLoginMethodDeniedForOrganization')
  );
};

export interface LoginMethodDeniedDetails {
  orgId: string;
  authType: string;
}

export const parseLoginMethodDeniedError = (
  error: RpcError
): LoginMethodDeniedDetails | null => {
  // NOTE: this regex must stay in sync with the backend message format produced by
  // LoginMethodDeniedForOrganization in the app repo's errors.go. We only extract the
  // org ID and the denied method here (used to gate and route to the denial page); the
  // set of allowed methods is read from the organization API on that page rather than
  // parsed from the message. Any trailing clauses in the message are ignored.
  const regex =
    /errLoginMethodDeniedForOrganization: organization '(?<orgId>[^']+)' does not allow login method '(?<authType>[^']+)'/u;
  const match = regex.exec(error.message);
  if (
    match?.groups?.orgId === undefined ||
    match.groups.authType === undefined
  ) {
    return null;
  }
  return {
    orgId: match.groups.orgId,
    authType: match.groups.authType,
  };
};

export const formatLoginMethodDisplay = (authType: string): string => {
  switch (authType) {
    case 'PASSWORD': {
      return 'password';
    }
    case 'GOOGLE': {
      return 'Google';
    }
    case 'OPENID_CONNECT': {
      return 'GitHub';
    }
    case 'APPLE': {
      return 'Apple';
    }
    default: {
      return authType;
    }
  }
};

export const isInvalidArgumentError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.INVALID_ARGUMENT]
  );
};

/** The service or method is not registered on this deployment. */
export const isUnimplementedError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.UNIMPLEMENTED]
  );
};

export const isResourceExhaustedError = (error: unknown): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.RESOURCE_EXHAUSTED]
  );
};

export const errorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Some libraries throw Error() with an empty message (e.g. protobuf-ts assert).
    // The chat UI hides falsy error strings, so never return "".
    return error.message.trim() === ''
      ? 'Something went wrong.'
      : error.message;
  }
  return String(error);
};

/**
 * Extracts the organization identifier from the specific “overdue bill” clause
 * in a given error message.
 * @param errorMessage The error.message string to parse.
 * @returns The organization identifier inside the quotes after
 *          “You do not have access until you pay your overdue bill for organization”,
 *          or null if that exact phrase isn’t present.
 */
export const getPaymentURLFromPaymentRequiredError = (
  error: RpcError
): string | null => {
  // match only the overdue-bill clause, with a named capture, Unicode + case-insensitive
  const regex = /overdue bill for organization\s*['"](?<orgId>[^'"]+)['"]/iu;
  const match = regex.exec(error.message);

  const orgId = match?.groups?.orgId;
  if (orgId === undefined) {
    return null;
  }
  return `/organizations/${orgId}/payment?mode=payment`;
};

export const isConfigUpdateConflictsError = (
  error: unknown
): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.FAILED_PRECONDITION] &&
    error.message.includes('has been updated since last known update')
  );
};

export const extractEditorInfoFromConfigUpdateConflictsError = (
  error: RpcError
): string | undefined => {
  return error.message.split('Last edited by: ')[1];
};

/** Stable FailedPrecondition message from RunConfigAssistantTurn when the per-request prompt no longer fits. */
export const CONVERSATION_TOO_LONG = 'conversation_too_long';

export const isConversationTooLongError = (
  error: unknown
): error is RpcError => {
  return (
    error instanceof RpcError &&
    error.code === GrpcStatusCode[GrpcStatusCode.FAILED_PRECONDITION] &&
    error.message.includes(CONVERSATION_TOO_LONG)
  );
};

export type { RpcError as APIError } from '@protobuf-ts/runtime-rpc';
